/** 上传链路分步重试：共 3 次尝试（首次 + 2 次重试），第 3 次失败后终止 */

export const UPLOAD_MAX_ATTEMPTS = 3
export const PRE_SIGN_TIMEOUT_MS = 15_000

/** 第 n 次失败后的基础等待（ms）；仅前两次失败会等待后重试 */
export const RETRY_BASE_DELAYS_MS = [500, 1000]

const JITTER_RATIO = 0.3

const RETRYABLE_HTTP_STATUS = new Set([408, 425, 429, 500, 502, 503, 504])
const RETRYABLE_BUSINESS_CODES = new Set([100060])
const NON_RETRYABLE_REASONS = new Set([
  'auth_required',
  'invalid_format',
  'oversize',
  'invalid_param',
])

export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * 指数退避 + jitter：
 * - 第 1 次失败后：500ms + random(0 .. 500*0.3)
 * - 第 2 次失败后：1000ms + random(0 .. 1000*0.3)
 * - 第 3 次失败后：不再等待，由调用方终止
 * @param {number} failureIndex 0 = 第 1 次失败后；1 = 第 2 次失败后
 */
export function retryDelayMs(failureIndex) {
  const index = Math.max(0, Math.min(failureIndex, RETRY_BASE_DELAYS_MS.length - 1))
  const base = RETRY_BASE_DELAYS_MS[index]
  const jitter = Math.floor(Math.random() * (base * JITTER_RATIO + 1))
  return base + jitter
}

export function isAbortError(error) {
  if (!error) return false
  if (error.name === 'AbortError') return true
  const msg = String(error.message || error || '')
  return /aborted|AbortError/i.test(msg)
}

export function isRetryableUploadError(error) {
  if (!error) return false

  const reason = String(error.failReason || '')
  if (NON_RETRYABLE_REASONS.has(reason)) return false

  // 兄弟分片取消等 Abort，不重试；预签名超时走 failReason=timeout
  if (isAbortError(error) && reason !== 'timeout') return false

  const status = Number(error.httpStatus ?? error.status)
  if (status === 401 || status === 403) return false

  if (
    reason === 'timeout' ||
    reason === 'network_error' ||
    reason === 'sign_failed'
  ) {
    return true
  }

  if (Number.isFinite(status) && RETRYABLE_HTTP_STATUS.has(status)) return true

  const businessCode = Number(error.businessCode ?? error.code)
  if (Number.isFinite(businessCode) && RETRYABLE_BUSINESS_CODES.has(businessCode)) {
    return true
  }

  const msg = String(error.message || '')
  if (/network|fetch failed|load failed|timeout/i.test(msg)) return true

  return false
}

/**
 * @template T
 * @param {() => Promise<T>} fn
 * @param {{ isRetryable?: (error: unknown) => boolean, signal?: AbortSignal }} [options]
 */
export async function withUploadRetry(fn, options = {}) {
  const { isRetryable = isRetryableUploadError, signal } = options
  let lastError

  for (let attempt = 0; attempt < UPLOAD_MAX_ATTEMPTS; attempt++) {
    if (signal?.aborted) {
      throw lastError || new Error('upload aborted')
    }
    try {
      return await fn(attempt)
    } catch (error) {
      lastError = error
      const failureCount = attempt + 1
      // 第 3 次失败后终止；仅第 1、2 次失败后等待再重试
      const canRetry =
        failureCount < UPLOAD_MAX_ATTEMPTS &&
        !signal?.aborted &&
        isRetryable(error)
      if (!canRetry) throw error
      await sleep(retryDelayMs(attempt))
    }
  }

  throw lastError
}

/**
 * 带超时的 fetch 包装：超时抛出带 failReason=timeout 的错误（由调用方规范化）。
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(fetchFn, timeoutMs = PRE_SIGN_TIMEOUT_MS) {
  if (typeof AbortController === 'undefined') {
    return fetchFn(undefined)
  }

  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort()
  }, timeoutMs)

  try {
    return await fetchFn(controller.signal)
  } catch (error) {
    if (isAbortError(error) || controller.signal.aborted) {
      const timeoutError = new Error(`request timed out after ${timeoutMs}ms`)
      timeoutError.name = 'TimeoutError'
      timeoutError.failReason = 'timeout'
      timeoutError.cause = error
      throw timeoutError
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}
